import { CrumpetCorner } from '../../model/components/CrumpetCorner';
import {
    getDefaultPollByType,
    ThisOrThatArticlePage,
} from '../../model/components/PollArticlePage';
import { isThisOrThatPost } from '../../model/PostTypes';
import { ThisOrThatPostData } from '../../model/types/ThisOrThatPostData';
import { PathContextProvider } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { CrumpetCornerInput } from './CrumpetCornerInput';
import { MediaInput } from './MediaInput';
import { PollThisOrThatInput } from './PollThisOrThatInput';
import { TextTypeInput } from './TextTypeInput';
import { ThisOrThatPollData } from '@lifeserver/shared';

import { assert } from '../../../utils/assert';

import styles from './ThisOrThatPostInput.module.scss';

export interface ThisOrThatPostInputProps {
    value: ThisOrThatPostData;
    onChange?: (value: ThisOrThatPostData) => void;
}

export const ThisOrThatPostInput = ({
    value,
    onChange,
}: ThisOrThatPostInputProps) => {
    const { post, validations, isEditingExistingPost, initialPostState } =
        usePortalPostEditor();

    const canModifyPolls =
        !isEditingExistingPost || initialPostState !== 'ready';

    assert(
        isThisOrThatPost(post),
        'ThisOrThatPostInput must be rendered with a ThisOrThat post',
    );

    const [frontPage, backPage] = value.pages;

    const onFrontPageChange = (page: ThisOrThatArticlePage) => {
        onChange?.({
            ...value,
            pages: [page, ...(backPage ? [backPage] : [])],
        });
    };

    const onBackPageChange = (page: ThisOrThatArticlePage) => {
        onChange?.({
            ...value,
            pages: [frontPage, page],
        });
    };

    const toggleBackPage = (enabled: boolean) => {
        if (enabled && !backPage) {
            const newBackPage: ThisOrThatArticlePage = {
                background: undefined,
                header: undefined,
                body: undefined,
                popTexts: undefined,
                crumpetCorner: undefined,
                poll: undefined,
            };
            onChange?.({
                ...value,
                pages: [frontPage, newBackPage],
            });
        } else if (!enabled && backPage) {
            // Remove the back page and clear crumpet corner from front page
            const updatedFrontPage: ThisOrThatArticlePage = {
                ...frontPage,
                crumpetCorner: undefined,
            };
            onChange?.({
                ...value,
                pages: [updatedFrontPage],
            });
        }
    };

    const onCrumpetCornerChange = (crumpetCorner?: CrumpetCorner) => {
        onFrontPageChange({
            ...frontPage,
            crumpetCorner,
        });
    };

    const createDefaultPoll = (): ThisOrThatPollData =>
        getDefaultPollByType('ThisOrThat');

    const toggleFrontPagePoll = (enabled: boolean) => {
        onFrontPageChange({
            ...frontPage,
            poll: enabled ? createDefaultPoll() : undefined,
        });
    };

    const toggleBackPagePoll = (enabled: boolean) => {
        onBackPageChange({
            ...backPage,
            poll: enabled ? createDefaultPoll() : undefined,
        });
    };

    const hasPoll = frontPage.poll || backPage?.poll;
    const hasValidationError = !hasPoll;

    return (
        <div>
            {hasValidationError && (
                <div className={styles.validationError}>
                    At least one page must have a poll configured.
                </div>
            )}

            <div className={styles.container}>
                <fieldset>
                    <legend>Front Page</legend>

                    <fieldset>
                        <legend>Background</legend>
                        <MediaInput
                            value={frontPage.background}
                            onChange={(background) =>
                                onFrontPageChange({
                                    ...frontPage,
                                    background,
                                })
                            }
                        />
                    </fieldset>

                    {backPage && (
                        <fieldset>
                            <legend>Crumpet Corner</legend>
                            <CrumpetCornerInput
                                value={frontPage.crumpetCorner}
                                onChange={onCrumpetCornerChange}
                            />
                        </fieldset>
                    )}
                    <PathContextProvider segment="pages">
                        <PathContextProvider segment={0}>
                            <TextTypeInput
                                value={frontPage}
                                onChange={(updatedPage) => {
                                    const thisOrThatPage: ThisOrThatArticlePage =
                                        {
                                            ...updatedPage,
                                            poll: frontPage.poll,
                                        };
                                    onFrontPageChange(thisOrThatPage);
                                }}
                                pageIndex={0}
                                validations={validations}
                            />
                        </PathContextProvider>
                    </PathContextProvider>
                    {(canModifyPolls || frontPage.poll) && (
                        <fieldset>
                            <legend>Poll</legend>
                            {canModifyPolls && (
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(frontPage.poll)}
                                        onChange={(e) =>
                                            toggleFrontPagePoll(
                                                e.target.checked,
                                            )
                                        }
                                    />
                                    Include poll on front page
                                </label>
                            )}

                            {frontPage.poll && (
                                <PollThisOrThatInput
                                    value={frontPage.poll}
                                    onChange={(poll) =>
                                        onFrontPageChange({
                                            ...frontPage,
                                            poll: poll,
                                        })
                                    }
                                />
                            )}
                        </fieldset>
                    )}
                </fieldset>

                <fieldset>
                    <legend>Back Page</legend>
                    <label>
                        <input
                            type="checkbox"
                            checked={Boolean(backPage)}
                            onChange={(e) => toggleBackPage(e.target.checked)}
                        />
                        Include back page
                    </label>

                    {backPage && (
                        <>
                            <fieldset>
                                <legend>Background</legend>
                                <MediaInput
                                    value={backPage.background}
                                    onChange={(background) =>
                                        onBackPageChange({
                                            ...backPage,
                                            background,
                                        })
                                    }
                                />
                            </fieldset>
                            <PathContextProvider segment="pages">
                                <PathContextProvider segment={1}>
                                    <TextTypeInput
                                        value={backPage}
                                        onChange={(updatedPage) => {
                                            const thisOrThatBackPage: ThisOrThatArticlePage =
                                                {
                                                    ...updatedPage,
                                                    poll: backPage.poll,
                                                };
                                            onBackPageChange(
                                                thisOrThatBackPage,
                                            );
                                        }}
                                        pageIndex={1}
                                        validations={validations}
                                    />
                                </PathContextProvider>
                            </PathContextProvider>
                            {(canModifyPolls || backPage.poll) && (
                                <fieldset>
                                    <legend>Poll</legend>
                                    {canModifyPolls && (
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(backPage.poll)}
                                                onChange={(e) =>
                                                    toggleBackPagePoll(
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                            Include poll on back page
                                        </label>
                                    )}

                                    {backPage.poll && (
                                        <PollThisOrThatInput
                                            value={backPage.poll}
                                            onChange={(poll) =>
                                                onBackPageChange({
                                                    ...backPage,
                                                    poll: poll,
                                                })
                                            }
                                        />
                                    )}
                                </fieldset>
                            )}
                        </>
                    )}
                </fieldset>
            </div>
        </div>
    );
};
